<template lang="pug">
#other-user-group-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 施設関係者グループ情報修正

    .box-body.register-body
      FormInputSelect(
        key-name="種類"
        required
        name="group-type"
        v-model="groupTypeId"
        :options="otherGroupTypeOptions"
        :validation-rules="{ required: true }"
        :styles="{ 'max-width': '540px' }"
      )
      FormInput(
        key-name="グループ名"
        required
        name="other-user-group-name"
        v-model="name"
        type="text"
        placeholder="グループ名"
        :maxlength="32"
        :validation-rules="{ required: true, max: 32 }"
        :styles="{ width: '340px' }")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmModal()" type="button") 更新
        button.btn.cancel-btn(@click="gotoOtherUserGroupList()" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="施設関係者グループ情報を更新しますか？" @ok="updateOtherUserGroup")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoOtherUserGroupList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { OtherUserGroupListState, SendMessagesState } from "@/store/NeedRefreshes";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";
import OtherUserGroupsService, { OtherUserGroupsPatchModel } from "@/commons/services/OtherUserGroups";
import OtherUserGroupModel from "@/commons/models/OtherUserGroup";
import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";

/**
 * 利用者登録画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    FormInput,
    FormInputSelect
  },
  /**
   * #Topic:
   * バリデーターを使いたいコンポーネントは以下を記載してバリデーターをnewする
   */
  $_veeValidate: { validator: "new" }
})
export default class OtherUserGroupEdit extends Vue {
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** グループ名 */
  name: string = "";
  /** グループ種類 */
  groupTypeId: any = "";
  /** グループ種類名セレクトボックス用項目 */
  otherGroupTypeOptions: Array<{ value: number | string; label: string }> = [];
  /** 整合性チェック用更新日時 */
  updated: number = 0;
  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * vue lifecycle created
   */
  public async mounted(): Promise<void> {
    CustomValidate.addCustomValidate(this.$validator);

    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherGroupTypes();
    const otherGroupTypeOptions: Array<{ value: number; label: string }> = this.createOtherUserGroupTypeOptions(otherUserGroupTypes);
    this.otherGroupTypeOptions = otherGroupTypeOptions;

    const group: OtherUserGroupModel = await this.getPageOtherUserGroup();
    this.name = group.name;
    this.groupTypeId = group.groupTypeId;
    this.updated = group.updated;

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);

    return otherUserGroupTypes;
  }

  /**
   * ### 種類のセレクトボックス用のパラメータを作成する
   */
  public createOtherUserGroupTypeOptions(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Array<{ value: number; label: string }> {
    let otherGroupTypeOptions: Array<{ value: number; label: string }> = [];

    for (const type of otherUserGroupTypes) {
      otherGroupTypeOptions.push({ value: type.id, label: type.name });
    }

    return otherGroupTypeOptions;
  }

  /**
   * 編集対象の施設関係者グループを作成する
   */
  public async getPageOtherUserGroup(): Promise<OtherUserGroupModel> {
    const response: OtherUserGroupModel = await this.otherUserGroupsService.get(userStore, Number(this.$route.params.id));
    return response;
  }

  /**
   * ### 更新確認モーダルを表示する
   */
  public async showConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### 施設関係者グループ更新
   */
  public async updateOtherUserGroup(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const requestModel: OtherUserGroupsPatchModel = {
        name: this.name,
        groupTypeId: this.groupTypeId,
        updated: this.updated
      };
      await this.otherUserGroupsService.patch(userStore, Number(this.$route.params.id), requestModel);

      // 施設関係者を再読み込みさせるようstoreを更新
      let otherUserGroupList: OtherUserGroupListState = needRefreshesStore.otherUserGroupList;
      otherUserGroupList.otherUserGroupList = true;
      needRefreshesStore.setOtherUserGroupList(otherUserGroupList);
      // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
      let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
      sendMessageState.sendMessages = true;
      needRefreshesStore.setSendMessage(sendMessageState);

      // 更新成功ダイアログを表示する
      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 施設関係者グループ一覧に遷移する
   */
  public gotoOtherUserGroupList(): void {
    this.$router.push(PcUrl.OtherUserGroupList);

    return;
  }
}
</script>

<style lang="scss" scoped>
#other-user-group-edit {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
